import { Component, OnInit } from '@angular/core';
import { CommonModule } from "@angular/common";
import { RouterLink } from "@angular/router";
import { ClrIconModule } from "@clr/angular";
import '@cds/core/icon/register.js';
import { ClarityIcons, dashboardIcon, hardDiskIcon, storageIcon, twoWayArrowsIcon } from "@cds/core/icon";
import { AuthService } from "../../_shared/services/auth.service";
import { StatusResponse } from "../../_shared/interfaces";

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ClrIconModule,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.sass'
})
export class SettingsComponent implements OnInit {
  status: StatusResponse | null = null;
  isAdmin = false;
  loading = false;
  lastUpdated: Date | null = null;

  constructor(
    private authService: AuthService
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon, storageIcon, hardDiskIcon, dashboardIcon);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.reload();
  }

  /**
   * Holt den Status neu. Alle drei Karten haengen an einem einzigen
   * /rest/status-Aufruf, ein Klick aktualisiert also immer alle drei.
   */
  reload(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.authService.rmsStatus().subscribe((status) => {
      this.status = status;
      this.lastUpdated = new Date();
      this.loading = false;
    });
  }

  get minioStatusText(): string {
    if (!this.status) return this.lastUpdated ? 'nicht erreichbar' : 'Lade...';
    if (!this.status.minio?.configured) return 'nicht konfiguriert';
    return this.status.minio.ok ? 'OK' : 'Fehler';
  }

  get minioReadyText(): string {
    const value = this.status?.minio?.ready;
    if (value === undefined || value === null) return '-';
    return value ? 'ready' : 'not ready';
  }

  get minioLiveText(): string {
    const value = this.status?.minio?.live;
    if (value === undefined || value === null) return '-';
    return value ? 'live' : 'down';
  }

  get dbStatusText(): string {
    if (!this.status) return this.lastUpdated ? 'nicht erreichbar' : 'Lade...';
    return this.status.db?.ok ? 'OK' : 'Fehler';
  }

  get dbName(): string {
    return this.status?.db?.name ?? '-';
  }

  get dbHost(): string {
    return this.status?.db?.host ?? '-';
  }

  get minioEndpoint(): string {
    return this.status?.minio?.endpoint ?? '-';
  }

  get minioBucket(): string {
    return this.status?.minio?.bucket ?? '-';
  }

  get minioRegion(): string {
    return this.status?.minio?.region ?? '-';
  }

  get minioAccessKey(): string {
    return this.status?.minio?.access_key_masked ?? '-';
  }

  get minioBucketExistsText(): string {
    const value = this.status?.minio?.bucket_exists;
    if (value === undefined || value === null) return '-';
    return value ? 'ja' : 'nein';
  }

  get minioBucketVersioning(): string {
    return this.status?.minio?.bucket_versioning ?? '-';
  }

  get minioBucketPolicy(): string {
    return this.status?.minio?.bucket_policy ?? '-';
  }

  get minioError(): string {
    return this.status?.minio?.error ?? '';
  }

  get dbError(): string {
    return this.status?.db?.error ?? '';
  }

  get dbVersion(): string {
    return this.status?.db?.version ?? '-';
  }

  get dbUptime(): string {
    const value = this.status?.db?.uptime_seconds;
    if (value === undefined || value === null) return '-';
    return formatSeconds(value);
  }

  get dbSize(): string {
    const value = this.status?.db?.size_bytes;
    if (value === undefined || value === null) return '-';
    return formatBytes(value);
  }

  get dbThreadsConnected(): string {
    const value = this.status?.db?.threads_connected;
    if (value === undefined || value === null) return '-';
    return value.toString();
  }

  get dbMaxConnections(): string {
    const value = this.status?.db?.max_connections;
    if (value === undefined || value === null) return '-';
    return value.toString();
  }

  get tracingEnabledText(): string {
    const enabled = this.status?.tracing?.enabled;
    if (enabled === undefined || enabled === null) return '-';
    return enabled ? 'aktiv' : 'deaktiviert';
  }

  get tracingEndpoint(): string {
    return this.status?.tracing?.otlp_endpoint ?? '-';
  }

  get tracingServiceName(): string {
    return this.status?.tracing?.service_name ?? '-';
  }
}

function formatSeconds(total: number): string {
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
}
